<template>
  <ion-card class="card-rental-coupon" @click="onCouponClick">
    <ion-card-content>
      <div class="floating-actions">
        <ion-button
          v-if="rentalCoupon.status === 'send'"
          fill="outline"
          size="small"
          color="secondary"
        >
          <ion-icon slot="icon-only" :icon="eyeOutline"></ion-icon>
        </ion-button>

        <ion-button
          v-if="
            rentalCoupon.status === 'save' &&
            !rentalCoupon.signature_source &&
            !rentalCoupon.no_signature_source
          "
          size="small"
          color="danger"
          @click.stop="presentDeleteCoupon"
        >
          <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
        </ion-button>
      </div>

      <div class="info">
        <ion-label>n° {{ rentalCoupon.name }}</ion-label>
        <ion-label class="label-date">{{ date }}</ion-label>
      </div>

      <div class="history">
        <div
          v-if="
            rentalCoupon.signature_source || rentalCoupon.no_signature_source
          "
        >
          <ion-icon
            class="width-icon-img-20"
            :icon="mailOpenOutline"
          ></ion-icon>
          <ion-label>envoyé au chargement</ion-label>
        </div>

        <div v-if="rentalCoupon.status === 'send'">
          <ion-icon
            class="width-icon-img-20"
            :icon="mailOpenOutline"
          ></ion-icon>
          <ion-label>envoyé au contact</ion-label>
        </div>
      </div>

      <div class="actions">
        <div v-if="isShareable">
          <ion-button
            class="coupon-button"
            fill="outline"
            size="small"
            color="tertiary"
            @click.stop="presentShare(rentalCoupon)"
          >
            <ion-icon slot="icon-only" :icon="idCardOutline"></ion-icon>
            Présenter la LV
          </ion-button>
        </div>

        <div v-if="rentalCoupon.status === 'save'">
          <ion-button
            class="coupon-button"
            fill="outline"
            size="small"
            color="success"
          >
            <ion-icon slot="icon-only" :icon="pencilOutline"></ion-icon>
            Poursuivre
          </ion-button>
        </div>

        <div v-if="rentalCoupon.status === 'send'">
          <ion-button
            class="coupon-button"
            fill="outline"
            size="small"
            color="success"
            @click.stop="resendCoupon"
          >
            <ion-icon slot="icon-only" :icon="mailOutline"></ion-icon>
            Renvoyer
          </ion-button>
        </div>
      </div>
    </ion-card-content>
  </ion-card>
</template>

<script>
import {
  IonLabel,
  IonButton,
  IonIcon,
  alertController,
  toastController,
  loadingController,
} from '@ionic/vue';
import {
  trashOutline,
  mailOutline,
  mailOpenOutline,
  sendOutline,
  idCardOutline,
  pencilOutline,
  eyeOutline,
} from 'ionicons/icons';
import { convertDateStartDateEndToLisibleDate } from '@/services/utils/dateUtils';
import {
  destroyRentalCoupon,
  destroyRentalCouponOutsourcing,
  sendRentalCoupon,
  sendRentalCouponOutsourcing,
} from '@/services/driver/rentalCoupon';
import utils from '@/services/utils/Utils';
import HasCouponSharing from '@/components/driver/rentalCoupon/mixins/HasCouponSharing';

const cancelButton = {
  text: 'Annuler',
  role: 'cancel',
  handler: () => {},
};

/**
 * <rental-coupon> component.
 *
 * Used in at least the following:
 * - List of **all** available rental coupons (`AllRentalCoupon.vue`)
 * - List of rental coupons for a **specific mission** (`ListeRentalCoupon.vue`)
 */
export default {
  name: 'RentalCoupon',
  mixins: [HasCouponSharing],
  components: {
    IonLabel,
    IonButton,
    IonIcon,
  },
  data() {
    return {
      flashMessage: '',
    };
  },
  props: {
    rentalCoupon: {},
    typeMission: String,
  },

  setup() {
    return {
      trashOutline,
      mailOutline,
      mailOpenOutline,
      sendOutline,
      idCardOutline,
      pencilOutline,
      eyeOutline,
    };
  },

  computed: {
    /**
     * Get date in desired format
     */
    date() {
      if (
        this.rentalCoupon &&
        this.rentalCoupon.start &&
        this.rentalCoupon.end
      ) {
        return convertDateStartDateEndToLisibleDate(
          this.rentalCoupon.start,
          this.rentalCoupon.end,
        );
      }
      return '';
    },

    isOutsourcing() {
      return (
        this.$route && this.$route.params && this.$route.params.outsourcingToken
      );
    },
  },

  methods: {
    async presentDeleteCoupon() {
      const alert = await alertController.create({
        header: 'Suppression du bon ' + this.rentalCoupon.name,
        message:
          'Voulez-vous réellement supprimer ' +
          (this.typeMission === 'reservation'
            ? 'ce bon de location ?'
            : 'cette lettre de voiture ?'),
        buttons: [
          cancelButton,
          {
            text: 'Valider',
            handler: async () => {
              const response = this.isOutsourcing
                ? await destroyRentalCouponOutsourcing(
                    this.$route.params.outsourcingToken,
                    this.rentalCoupon.id,
                  )
                : await destroyRentalCoupon(this.rentalCoupon.id);
              const toast = await toastController.create({
                message: response.flash_message,
                duration: 3000,
                color: 'success',
                position: 'top',
                cssClass: 'toast-rental-coupon',
              });
              await toast.present();

              this.refreshList();
            },
          },
        ],
      });

      return alert.present();
    },

    async resendCoupon() {
      const alert = await alertController.create({
        header:
          (this.typeMission === 'reservation'
            ? 'Renvoyer le BL '
            : 'Renvoyer la LV ') + this.rentalCoupon.name,
        message:
          'Voulez-vous réellement envoyer une nouvelle fois ' +
          (this.typeMission === 'reservation'
            ? 'ce bon de location ?'
            : 'cette lettre de voiture ?'),
        buttons: [
          cancelButton,
          {
            text: 'Valider',
            handler: async () => {
              const loading = await loadingController.create({
                message: "En cours d'envoi...",
              });
              await loading.present();

              let response;
              try {
                response = this.isOutsourcing
                  ? await sendRentalCouponOutsourcing(
                      this.$route.params.outsourcingToken,
                      this.rentalCoupon.id,
                    )
                  : await sendRentalCoupon(this.rentalCoupon.id);
              } catch (e) {
                await loading.dismiss();
                await utils.onError(e);
                return;
              }
              await loading.dismiss();

              const toast = await toastController.create({
                message: response.flash_message,
                duration: 3000,
                color: 'success',
                position: 'top',
                cssClass: 'toast-rental-coupon',
              });
              await toast.present();
              this.refreshList();
            },
          },
        ],
      });

      return alert.present();
    },

    async onCouponClick() {
      if (
        this.rentalCoupon.status === 'save' &&
        this.rentalCoupon.mission_reports_count
      ) {
        const alert = await alertController.create({
          header: 'Visualiser le rapport de mission',
          message:
            'Avant de compléter le bon de location, voulez-vous visualiser et compléter le rapport de mission ?',
          buttons: [
            {
              text: 'Non',
              handler: () => {
                this.goToRentalCoupon();
              },
            },
            {
              text: 'Oui',
              handler: () => {
                this.goToMissionReport();
              },
            },
          ],
        });

        return alert.present();
      }

      this.goToRentalCoupon();
    },

    goToRentalCoupon() {
      this.navigate('bonLocation');
    },

    goToMissionReport() {
      this.navigate('report');
    },

    /**
     * Navigate on either rental coupon page, or mission report part depending on "target" parameter.
     */
    navigate(target) {
      // when outsourcing there is no access (yet) to mission reports, so ignore target
      if (this.$route.params.outsourcingToken) {
        const prefix = utils.getOutsourcingUrlPrefix(this.typeMission);
        this.$router.push(
          `/sous-traitance/${prefix}/${this.$route.params.outsourcingToken}/bonLocation/${this.rentalCoupon.id}`,
        );
        return;
      }

      const couponId = this.rentalCoupon.id;
      const rideId = this.rentalCoupon.ride_id;

      if (this.typeMission === 'reservation') {
        this.$router.push(
          `/chantier/${this.rentalCoupon.reservation_id}/${rideId}/${target}/${couponId}`,
        );
        return;
      }

      if (this.typeMission === 'transfert') {
        this.$router.push(
          `/transfert/${this.rentalCoupon.transfer_id}/${rideId}/${this.rentalCoupon.item_id}/${target}/${couponId}`,
        );
        return;
      }

      this.$router.push(
        `/livraison/${this.rentalCoupon.delivery_id}/${rideId}/${this.rentalCoupon.item_id}/${target}/${couponId}`,
      );
    },

    refreshList() {
      // Ugly but can not fix without big refactor
      const refresher = document.querySelector(
        '.tabs-inner .refresher-coupons',
      );
      if (refresher) {
        refresher.click();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
ion-card {
  margin-left: 0;
  margin-right: 0;
  margin-top: 6px;
  margin-bottom: 12px;
}

ion-card-content {
  padding-top: 10px;
  padding-bottom: 10px;

  > div:not(:last-child) {
    margin-bottom: 5px;
  }
}

.floating-actions {
  position: absolute;
  top: 1px;
  right: 4px;

  ion-button {
    width: 30px !important;
    height: 24px !important;
    --padding-start: 5px;
    --padding-end: 5px;
  }
}

.info {
  ion-label {
    font-size: initial;
  }

  .label-date {
    color: black !important;
    margin-left: 8px;
  }
}

.history {
  &,
  ion-label {
    color: black !important;
  }

  > div {
    display: flex;
    align-items: center;
  }

  ion-icon {
    margin-right: 6px;
  }
}

.actions {
  display: flex;
  justify-content: space-between;

  > div {
    flex: 0 1 49%;
  }

  ion-button {
    width: 100%;
  }
}
</style>
